import React, { useState } from 'react';
import { IoSearch, IoAdd, IoVideocam, IoSync } from 'react-icons/io5';
import '../style/Groups.css';
import Modal from './Modal';
import { searchMusic } from '../api/MusicAPI';
import { useSocket } from '../socket/SocketContext';
import { useLanguage } from '../i18n/LanguageContext';
import { useLayoutMode } from '../hooks/useLayoutMode';
import { cleanMusicTitle } from '../utils/musicTitle';

const username = () => localStorage.getItem('username');
const token = () => localStorage.getItem('accessToken');

const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

// Deliberately not a strict video-file-extension check - a valid stream URL
// can be a bare .mp4/.m3u8 link but just as easily a signed CDN URL with no
// recognizable extension at all. All that matters here is "well-formed
// enough to hand to a <video>/hls.js element", which is exactly what
// `new URL()` already validates.
function isLikelyUrl(value) {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch (err) {
    return false;
  }
}

function isYouTubeUrl(value) {
  try {
    const host = new URL(value).hostname.replace(/^(www|m)\./, '');
    return host === 'youtube.com' || host === 'youtu.be';
  } catch (err) {
    return false;
  }
}

const PROBE_TIMEOUT_MS = 6000;

// Real playability check, not just a format guess - loads just enough of
// the URL for the browser to report whether it can actually decode it as
// video, mirroring (client-side) what the backend's HEAD/Content-Type
// check does server-side (see Backend/utils/videoUrlValidator.js) - two
// independent checks since a client-only one can be bypassed by anyone
// calling the socket API directly, and a server-only one means a bad link
// isn't caught until after the round-trip. Skipped for YouTube links
// (resolved server-side via yt-dlp instead) and .m3u8 links (Chromium has
// no native HLS support - see hls.js in VideoWatchOverlay.js - so a plain
// <video> element can't probe those at all; the backend's Content-Type
// check is the real gate for those).
function probeVideoUrl(url) {
  if (isYouTubeUrl(url) || /\.m3u8(\?|#|$)/i.test(url)) return Promise.resolve(true);
  return new Promise((resolve) => {
    const el = document.createElement('video');
    let settled = false;
    const finish = (ok) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      el.removeAttribute('src');
      el.load();
      resolve(ok);
    };
    const timer = setTimeout(() => finish(false), PROBE_TIMEOUT_MS);
    el.preload = 'metadata';
    el.addEventListener('loadedmetadata', () => finish(true), { once: true });
    el.addEventListener('error', () => finish(false), { once: true });
    el.src = url;
  });
}

// video:open's ack code (see socketServer.js) -> the specific reason toast,
// falling back to the generic failure message for anything else (denied
// permission mid-flight, e.g. NOT_IN_ROOM if they left right as they clicked).
function videoOpenFailureKey(res) {
  if (res.code === 'YOUTUBE_VIDEO_NOT_FOUND') return 'music.youtubeVideoNotFound';
  if (res.code === 'INVALID_VIDEO_URL') return 'music.invalidVideoUrl';
  return 'music.watchTogetherFailed';
}

// Search-and-play dialog for the music bot - same {iShow,setiShow}-controlled
// Modal pattern as the create-group/invite-friend dialogs in Groups.js.
// Lives in VoiceRoomPanel (the one component aware of the live room session
// globally) rather than Groups.js, since playing a track only makes sense
// once actually connected to a room's voice call.
export default function MusicSearchModal({ mState, roomId }) {
  const { enqueueMusic, openVideo, pushToast } = useSocket();
  const { t } = useLanguage();
  const { isMobile } = useLayoutMode();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null); // null = never searched yet
  const [searching, setSearching] = useState(false);
  const [pastedUrl, setPastedUrl] = useState('');
  const [checkingUrl, setCheckingUrl] = useState(false);

  const runSearch = async () => {
    const trimmed = query.trim();
    if (!trimmed || searching) return;
    setSearching(true);
    const res = await searchMusic(username(), token(), trimmed);
    setSearching(false);
    setResults(res.status === 'success' ? res.data.results : []);
  };

  // Deliberately leaves the modal open, unlike watchTogether below: lining up
  // several tracks in a row is the whole point of a queue, and reopening the
  // search for each one would make that needlessly tedious. The toast is what
  // confirms the click landed. (If nothing was playing the server starts this
  // one immediately - see MusicSession.enqueue.)
  const enqueue = async (result) => {
    const res = await enqueueMusic(roomId, {
      videoId: result.videoId,
      url: result.url,
      title: result.title,
      thumbnailUrl: result.thumbnailUrl,
      durationSec: result.durationSec,
      channel: result.channel
    });
    if (res.ok) return pushToast(t('music.addedToQueue'));
    pushToast(t(res.code === 'QUEUE_FULL' ? 'music.queueFull' : 'music.enqueueFailed'));
  };

  // Independent of the music bot (enqueue() above) - opens the synced "watch
  // together" overlay instead of streaming audio through LiveKit. videoId
  // is what the backend uses to tell a YouTube search result apart from a
  // pasted direct URL (see VideoWatchSession.open), so it's the only field
  // that differs between the two call sites below.
  const watchTogether = async (result) => {
    mState.setiShow(false);
    const res = await openVideo(roomId, {
      videoId: result.videoId,
      url: result.url,
      title: result.title,
      thumbnailUrl: result.thumbnailUrl,
      durationSec: result.durationSec
    });
    if (!res.ok) pushToast(t(videoOpenFailureKey(res)));
  };

  const watchFromUrl = async () => {
    const trimmed = pastedUrl.trim();
    if (!isLikelyUrl(trimmed) || checkingUrl) return;

    setCheckingUrl(true);
    const playable = await probeVideoUrl(trimmed);
    setCheckingUrl(false);
    if (!playable) {
      pushToast(t('music.invalidVideoUrl'));
      return;
    }

    mState.setiShow(false);
    const res = await openVideo(roomId, { url: trimmed, title: trimmed });
    if (!res.ok) pushToast(t(videoOpenFailureKey(res)));
  };

  return (
    <Modal mState={mState} buttonShow className="MusicSearchModalContainer">
      <h4 style={{ marginTop: 0 }}>{t('music.searchTitle')}</h4>
      <div className="MusicSearchInputRow">
        <input
          className="GroupNameInput"
          placeholder={t('music.searchPlaceholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
          autoFocus
        />
        <button onClick={runSearch} className="AddButton primaryColor" title={t('music.searchButton')}><IoSearch /></button>
      </div>

      {searching && <p className="descGray">{t('music.searching')}</p>}

      {!searching && results && (
        <ul className="MusicSearchResults">
          {results.length === 0 && <p className="descGray">{t('music.noResults')}</p>}
          {results.map((r) => (
            <li key={r.videoId} className="glassCard MusicSearchResultItem" onClick={() => enqueue(r)}>
              {r.thumbnailUrl
                ? <img src={r.thumbnailUrl} alt={r.title} />
                : <div className="MusicSearchResultThumbFallback" />}
              <div className="MusicSearchResultInfo">
                <b>{cleanMusicTitle(r.title)}</b>
                <aside className="descGray">
                  {r.channel}
                  {formatDuration(r.durationSec) && ` · ${formatDuration(r.durationSec)}`}
                </aside>
              </div>
              <IoAdd className="MusicSearchResultPlayIcon" title={t('music.addToQueue')} />
              {/* stopPropagation - the whole <li> already queues this result
                  on the music bot (see `enqueue` above); without it this
                  icon's click would bubble up and trigger both. On a phone
                  it's a labelled button of its own instead: a bare 18px
                  icon a finger-width from "queue this" made starting a
                  watch party for the whole room an easy mis-tap. */}
              {isMobile ? (
                <button
                  type="button"
                  className="MusicSearchWatchBtn secondaryColor"
                  onClick={(e) => { e.stopPropagation(); watchTogether(r); }}
                >
                  <IoVideocam /> {t('music.watchTogether')}
                </button>
              ) : (
                <IoVideocam
                  className="MusicSearchResultVideoIcon"
                  title={t('music.watchTogether')}
                  onClick={(e) => { e.stopPropagation(); watchTogether(r); }}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Independent of search - sometimes what people want to watch
          together isn't on YouTube at all, just a direct video stream link
          someone already has (see watchFromUrl above). */}
      <div className="MusicSearchUrlRow">
        <input
          className="GroupNameInput"
          placeholder={t('music.pasteUrlPlaceholder')}
          value={pastedUrl}
          onChange={(e) => setPastedUrl(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') watchFromUrl(); }}
        />
        <button
          onClick={watchFromUrl}
          className="AddButton primaryColor"
          title={checkingUrl ? t('music.checkingVideoUrl') : t('music.watchTogether')}
          disabled={!isLikelyUrl(pastedUrl.trim()) || checkingUrl}
        >
          {checkingUrl ? <IoSync className="MusicConnectingSpinner" /> : <IoVideocam />}
        </button>
      </div>

      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
        <button onClick={() => mState.setiShow(false)} className="SendButton secondaryColor">{t('common.close')}</button>
      </div>
    </Modal>
  );
}
